import { BarChart3 } from "lucide-react";

// Inline preview card for insight-kind tool answers — read_group counts,
// summarise_records, find_similar. Renders the top N buckets as a small
// table inside the assistant bubble. Presentational — no fetch, no state.

export interface InsightBucket {
    label: string;
    count: number;
}

export interface InsightPayload {
    title?: string;
    groupbyField?: string;
    buckets: InsightBucket[];
    totalCount?: number;
}

interface Props {
    title?: string;
    groupbyField?: string;
    buckets: InsightBucket[];
    totalCount?: number;
}

const MAX_ROWS = 3;

export function AssistantInsightPreview({ title, groupbyField, buckets, totalCount }: Props) {
    const top = buckets.slice(0, MAX_ROWS);
    const remaining = buckets.length - top.length;
    const total = totalCount ?? buckets.reduce((acc, b) => acc + b.count, 0);

    return (
        <div className="assistant-insight" data-testid="assistant-insight">
            <header className="assistant-insight__header">
                <BarChart3 size={12} />
                <span className="assistant-insight__title">
                    {title ?? (groupbyField ? `By ${groupbyField}` : "Summary")}
                </span>
                <span className="assistant-insight__total">{total.toLocaleString()} total</span>
            </header>
            {top.length > 0 ? (
                <ul className="assistant-insight__list">
                    {top.map((b, i) => (
                        <li key={`${b.label}-${i}`} className="assistant-insight__row">
                            <span className="assistant-insight__row-label">{b.label}</span>
                            <span className="assistant-insight__row-count">{b.count.toLocaleString()}</span>
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="assistant-insight__empty">No data.</p>
            )}
            {remaining > 0 && (
                <p className="assistant-insight__more">+{remaining} more</p>
            )}
        </div>
    );
}

/** Pure: detect when an assistant body is an insight payload (JSON-encoded)
    and return the parsed bucket list. Backend tools mark insight responses
    by wrapping the JSON inside a fenced ```insight … ``` block in the
    body OR by emitting a top-level { "__insight__": true, ... } object.
    Returns null when the body is regular prose. */
export function parseInsightBuckets(body: string): InsightPayload | null {
    if (!body) return null;

    // Fenced-block form — most reliable; the rest of the body can still be prose.
    const fenced = body.match(/```insight\s*\n([\s\S]*?)\n```/);
    if (fenced) {
        try {
            const obj = JSON.parse(fenced[1]) as Record<string, unknown>;
            return coerceInsightPayload(obj);
        } catch {
            return null;
        }
    }

    // Bare-JSON form — body starts with { and parses to a __insight__ object.
    const trimmed = body.trim();
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
        try {
            const obj = JSON.parse(trimmed) as Record<string, unknown>;
            if (obj.__insight__ === true) {
                return coerceInsightPayload(obj);
            }
        } catch {
            // not JSON — fall through
        }
    }

    return null;
}

function coerceInsightPayload(obj: Record<string, unknown>): InsightPayload | null {
    const rawBuckets = obj.buckets;
    if (!Array.isArray(rawBuckets)) return null;
    const buckets: InsightBucket[] = [];
    for (const b of rawBuckets) {
        if (!b || typeof b !== "object") continue;
        const r = b as Record<string, unknown>;
        const label = typeof r.label === "string" ? r.label : String(r.label ?? "");
        const count = typeof r.count === "number" ? r.count : Number(r.count);
        if (!Number.isFinite(count)) continue;
        buckets.push({ label, count });
    }
    return {
        title: typeof obj.title === "string" ? obj.title : undefined,
        groupbyField: typeof obj.groupby_field === "string" ? obj.groupby_field : undefined,
        totalCount: typeof obj.total_count === "number" ? obj.total_count : undefined,
        buckets,
    };
}
